import React from 'react';

import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box } from '@atlaskit/primitives/box';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Pressable, type PressableProps } from '@atlaskit/primitives/pressable';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { type XCSS, xcss } from '@atlaskit/primitives/xcss/xcss';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import type { FilterOrControlId } from '../../../constants/filters/types';
import { getIconColor } from './get-icon-color';
import { useIsRefreshedFilterButton } from './refreshed-filter-button-context';
import { getIcon } from './utils';

const defaultFilterButtonStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	gap: 'space.075',
	color: 'color.text.subtle',
	borderRadius: token('radius.small'),
	paddingInline: 'space.150',
	paddingBlock: 'space.050',
	maxHeight: '32px',
	minHeight: '32px',
	backgroundColor: 'color.background.neutral.subtle',
	border: `${token('border.width')} solid ${token('color.border')}`,
	whiteSpace: 'nowrap',
	font: 'font.body',
	fontWeight: 'font.weight.medium',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	maxWidth: '280px',
});

const wideStyles = xcss({
	width: '100%', // Allow the button to take up full width
});

const compactContentFitStyles = xcss({
	// This allows us to fit Dropdown button + App Selector + 2 product filters
	maxWidth: '180px',
});

const filterButtonSelectedStateStyles = xcss({
	backgroundColor: 'color.background.selected',
	color: 'color.text.selected',
	':hover': {
		backgroundColor: 'color.background.selected.hovered',
	},
	border: `${token('border.width')} solid transparent`, // Keep border consistent with non-selected state
});

const filterButtonSelectedStateBorderStyles = xcss({
	border: `${token('border.width')} solid ${token('color.border.selected')}`,
});

const filterButtonSubtleStyles = xcss({
	border: `${token('border.width')} solid transparent`, // Keeps the box the same size as the bordered button
	':active': {
		backgroundColor: 'color.background.neutral.subtle.pressed',
	},
});

const filterButtonRefreshedPressedStyles = xcss({
	':active': {
		backgroundColor: 'color.background.neutral.subtle.pressed',
	},
});

const filterButtonRefreshedSelectedStyles = xcss({
	borderColor: 'color.border.selected',
	':active': {
		backgroundColor: 'color.background.selected.pressed',
	},
});

const compactSpacingStyles = xcss({
	paddingInline: 'space.050',
});

const filterButtonContentStyles = xcss({
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	flexGrow: 1, // Allow the content to take up available space
	textAlign: 'left',
});

const noBorderStyles = xcss({
	border: 0,
});

export type FilterButtonProps = {
	children?: React.ReactNode;
	isSelected?: boolean;
	iconBefore?: React.ReactNode;
	iconAfter?: React.ReactNode;
	/** Controls the amount of padding in the button. */
	spacing?: 'default' | 'compact';
	/** Controls the amount of truncation (aka `maxWidth`) of the content */
	contentFit?: 'default' | 'compact';
	/** Controls whether or not to show border */
	noBorder?: boolean;
	/** Text to show in tooltip */
	tooltip?: React.ReactNode;
	filterOrControlId?: FilterOrControlId;
	hasChevron?: boolean;
	isFilterInDropdown?: boolean;
	xcss?: XCSS;
};

export const FilterButton: React.ForwardRefExoticComponent<
	React.PropsWithoutRef<PressableProps & FilterButtonProps> & React.RefAttributes<HTMLButtonElement>
> = React.forwardRef<HTMLButtonElement, PressableProps & FilterButtonProps>(function FilterButton(
	{
		children,
		contentFit = 'default',
		iconAfter,
		iconBefore,
		isSelected,
		noBorder,
		tooltip,
		spacing = 'default',
		filterOrControlId,
		hasChevron = true,
		isFilterInDropdown,
		xcss,
		...pressableProps
	},
	ref,
) {
	const isRefreshed = useIsRefreshedFilterButton();
	const isSubtle = isRefreshed && !isFilterInDropdown;
	const filterIcon = filterOrControlId
		? getIcon({ filterOrControlId, isSelected: Boolean(isSelected) })
		: null;

	const chevronIcon = hasChevron ? (
		<ChevronDownIcon label="" color={getIconColor(isSelected)} size="small" />
	) : null;

	return (
		<Tooltip content={tooltip ?? null} position="bottom">
			{(tooltipProps) => (
				<Box {...tooltipProps}>
					<Pressable
						{...pressableProps}
						xcss={[
							defaultFilterButtonStyles,
							isSubtle && !isSelected && filterButtonSubtleStyles,
							isRefreshed && !isSelected && filterButtonRefreshedPressedStyles,
							isSelected && filterButtonSelectedStateStyles,
							!isFilterInDropdown && isSelected && filterButtonSelectedStateBorderStyles,
							isRefreshed && isSelected && filterButtonRefreshedSelectedStyles,
							isFilterInDropdown && wideStyles,
							spacing === 'compact' && compactSpacingStyles,
							contentFit === 'compact' && compactContentFitStyles,
							noBorder && noBorderStyles,
							xcss,
						]}
						ref={ref}
					>
						{iconBefore}
						{filterIcon}
						{children && <Box xcss={filterButtonContentStyles}>{children}</Box>}
						{iconAfter}
						{chevronIcon}
					</Pressable>
				</Box>
			)}
		</Tooltip>
	);
});
